import {
  fetchAvailability,
  fetchDealerCampaigns,
  fetchPricing,
} from "../services/checkout.service";
import { CampaignTile, CampaignTilesResponse } from "@/types/checkout";
import {
  isAlreadyBookedByUser,
  isFutureMonth,
  formatMonthYear,
  formatPrice,
  formatCampaignMonth,
} from "./parsedata.utils";

interface Selection {
  month: string;
  year: string;
  price: number;
}

interface CampaignMonth {
  campaignMonth: string;
}

export interface CampaignTilesError {
  message: string;
  failedService: "pricing" | "availability" | "campaigns";
}

const CANCELLATION_DEADLINE_DAYS = 15;

export const isPastCancellationDeadline = (
  campaignMonth: number,
  campaignYear: number,
): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate the cancellation deadline (15 days before campaign start)
  const deadline = new Date(
    campaignYear,
    campaignMonth - 1,
    1 - CANCELLATION_DEADLINE_DAYS,
  );
  deadline.setHours(0, 0, 0, 0);

  return today > deadline;
};

export const isPreviousMonth = (
  campaignMonth: number,
  campaignYear: number,
): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  return (
    campaignYear < currentYear ||
    (campaignYear === currentYear && campaignMonth < currentMonth)
  );
};

export const fetchCampaignTiles = async (
  dealerId: string,
  limit: number,
): Promise<CampaignTilesResponse> => {
  const results = await Promise.allSettled([
    fetchPricing(dealerId),
    fetchAvailability(dealerId, limit),
    fetchDealerCampaigns(dealerId, "SCHEDULED", limit),
  ]);

  const [pricingResult, availabilityResult, campaignsResult] = results;

  if (pricingResult.status === "rejected") {
    const error: CampaignTilesError = {
      message: `Failed to fetch pricing: ${pricingResult.reason}`,
      failedService: "pricing",
    };
    throw error;
  }
  if (availabilityResult.status === "rejected") {
    const error: CampaignTilesError = {
      message: `Failed to fetch availability: ${availabilityResult.reason}`,
      failedService: "availability",
    };
    throw error;
  }
  if (campaignsResult.status === "rejected") {
    const error: CampaignTilesError = {
      message: `Failed to fetch campaigns: ${campaignsResult.reason}`,
      failedService: "campaigns",
    };
    throw error;
  }

  const pricing = pricingResult.value;
  const availability = availabilityResult.value;
  const campaigns = campaignsResult.value;

  const userBookedMonths = new Set(
    campaigns.campaigns
      .filter(isAlreadyBookedByUser)
      .map((c) => c.campaignMonth),
  );

  const tiles: CampaignTile[] = availability.featuredDealerAvailability
    .filter((item) => isFutureMonth(item.featuredDealerMonth))
    .map((item) => {
      const { month, year } = formatMonthYear(item.featuredDealerMonth);
      const isAvailable = item.featuredDealerAvailabilityStatus === "AVAILABLE";
      const isBookedByUser = userBookedMonths.has(item.featuredDealerMonth);

      const getSubtext = (): string => {
        if (isBookedByUser) return "Already scheduled";
        if (!isAvailable) return "Sold out";
        return formatPrice(pricing.featuredDealerBasePrice || 0); // TODO: determine how to handle missing pricing
      };

      return {
        id: item.featuredDealerMonth,
        month,
        year,
        price: pricing.featuredDealerBasePrice || 0, // TODO: determine how to handle missing pricing
        subtext: getSubtext(),
        available: isAvailable,
        alreadyBooked: isBookedByUser,
      };
    });

  return { tiles };
};

export const convertSelectionsToCampaigns = (
  selections: Selection[],
): CampaignMonth[] => {
  return selections.map((selection) => ({
    campaignMonth: formatCampaignMonth(selection.month, selection.year),
  }));
};
